import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

// ========================================
// TYPES
// ========================================
/** Response shape from GET /api/reports/gig. All metrics are pre-aggregated server-side. */
export interface GigReportResponse {
    totalRows: number;
    approvedRows: number;
    rejectedRows: number;
    totalGigHours: number;
    gigShiftPickup: number;
    gigSwapPickup: number;
}

interface GigReportState {
    gigReportData: GigReportResponse | null;
    loading: boolean;
    error: string | null;
}

const initialState: GigReportState = {
    gigReportData: null,
    loading: false,
    error: null,
};

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Fetch gig shift report metrics for a site with optional date range.
 * GET /api/reports/gig?siteCd=XX&startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
 */
export const fetchGigReport = createAsyncThunk(
    'gigReport/fetchGigReport',
    async ({ siteCd, startDate, endDate }: { siteCd: string; startDate?: string; endDate?: string }, { rejectWithValue }) => {
        try {
            const params: string[] = [`siteCd=${siteCd}`];
            if (startDate) params.push(`startDate=${startDate}`);
            if (endDate) params.push(`endDate=${endDate}`);

            const url = `${ENDPOINTS.GET_GIG_REPORT}?${params.join('&')}`;
            const response = await API_FETCH(url);
            console.log('✅ Gig Report API Response:', response);
            return response as GigReportResponse;
        } catch (error: any) {
            console.error('❌ Gig Report API Error:', error);
            return rejectWithValue(error?.message || 'Failed to fetch gig report data');
        }
    }
);

// ========================================
// SLICE
// ========================================
const gigReportSlice = createSlice({
    name: 'gigReport',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGigReport.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGigReport.fulfilled, (state, action) => {
                state.loading = false;
                state.gigReportData = action.payload;
            })
            .addCase(fetchGigReport.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = gigReportSlice.actions;
export default gigReportSlice.reducer;
